import { JSX } from 'preact';

import { EditorProps, ViewerProps, RichProp } from 'client/courses/components/page-builder';
import { CtaButton } from 'client/courses/components/page-builder/cta-button';
import { ContainerWrapper } from 'client/courses/components/page-builder/container-wrapper';

import { Color } from 'shared/colors';

import { CardLayoutBlock } from './card-layout-block';
import {
  extractReadonlyProps,
  extractBlockState,
  createGenerateState,
  SectionHeader,
} from './common-patterns';

import { CardStyles, PaddingSize } from 'server/types/public-overrides';
import { ServicesBlockState } from 'server/types';

const DEFAULT_CARD_STYLES: CardStyles = {
  borderRadius: 'xl',
  shadow: 'lg',
  borderWidth: 'none',
  backgroundType: 'solid',
  backgroundColor: '#ffffff',
  hoverLift: 'sm',
};

export const type = 'calendar-services';
export const name = 'Services';

export const initialState: ServicesBlockState = {
  headerRichText: '<h2>Choose Your Perfect Session</h2>',
  cards: [
    {
      id: crypto.randomUUID(),
      contentRichText:
        "<h3>Discovery Call</h3><p>A free 30-minute session to explore your goals and see if we're a good fit.</p>",
      durationRichText: '<p>30 minutes</p>',
      callToAction: {
        text: 'Book Discovery Call',
        url: '/book-me',
      },
    },
    {
      id: crypto.randomUUID(),
      contentRichText:
        '<h3>Strategy Session</h3><p>Deep dive into your challenges and create a personalized action plan.</p>',
      durationRichText: '<p>90 minutes</p>',
      callToAction: {
        text: 'Book Strategy Session',
        url: '/book-me',
      },
    },
    {
      id: crypto.randomUUID(),
      contentRichText:
        '<h3>Ongoing Coaching</h3><p>Weekly sessions with email support and accountability.</p>',
      durationRichText: '<p>4 sessions/month</p>',
      callToAction: {
        text: 'Start Coaching',
        url: '/book-me',
      },
    },
  ],
};

export const generateState = createGenerateState(initialState);

export const initialBlockState = {
  type,
  bgcolor: Color.gray50,
  paddingt: 'md' as PaddingSize,
  paddingb: 'md' as PaddingSize,
  cardStyles: DEFAULT_CARD_STYLES,
};

export function MiniView(): JSX.Element {
  return (
    <span class="block py-4">
      <span class="w-4/6 mx-auto block">
        <span class="block bg-gray-500 opacity-75 rounded-sm h-1 mb-3 mx-auto w-1/2"></span>
        <span class="grid grid-cols-2 gap-2">
          <span class="bg-gray-400 opacity-50 rounded p-1 flex flex-col">
            <span class="bg-gray-600 opacity-75 rounded-sm h-1 mb-1 w-3/4"></span>
            <span class="bg-gray-500 opacity-50 rounded-sm h-1 mb-1 w-full"></span>
            <span class="bg-gray-500 opacity-50 rounded-sm h-1 mb-2 w-2/3"></span>
            <span class="bg-gray-600 opacity-75 rounded h-1 w-1/2"></span>
          </span>
          <span class="bg-gray-400 opacity-50 rounded p-1 flex flex-col">
            <span class="bg-gray-600 opacity-75 rounded-sm h-1 mb-1 w-3/4"></span>
            <span class="bg-gray-500 opacity-50 rounded-sm h-1 mb-1 w-full"></span>
            <span class="bg-gray-500 opacity-50 rounded-sm h-1 mb-2 w-2/3"></span>
            <span class="bg-gray-600 opacity-75 rounded h-1 w-1/2"></span>
          </span>
          <span class="bg-gray-400 opacity-50 rounded p-1 flex flex-col">
            <span class="bg-gray-600 opacity-75 rounded-sm h-1 mb-1 w-3/4"></span>
            <span class="bg-gray-500 opacity-50 rounded-sm h-1 mb-1 w-full"></span>
            <span class="bg-gray-500 opacity-50 rounded-sm h-1 mb-2 w-2/3"></span>
            <span class="bg-gray-600 opacity-75 rounded h-1 w-1/2"></span>
          </span>
        </span>
      </span>
    </span>
  );
}

function View(
  props: EditorProps<ServicesBlockState> | ViewerProps<ServicesBlockState>,
): JSX.Element {
  const { editProps, readonly } = extractReadonlyProps(props);
  const blockState = extractBlockState(props);
  const cardStyles = blockState?.cardStyles || DEFAULT_CARD_STYLES;

  return (
    <ContainerWrapper>
      <SectionHeader state={props.state} setState={editProps?.setState} />

      <CardLayoutBlock
        blockType={type}
        itemType="Service"
        state={props.state}
        setState={editProps?.setState || (() => {})}
        emptyCard={{
          contentRichText: '<h3>New Service</h3><p>Describe your service here...</p>',
          durationRichText: '<p>Duration</p>',
          callToAction: {
            text: 'Book Service',
            url: '/book-me',
          },
        }}
        readonly={readonly}
        isBgLight={props.isBgLight}
        isSelected={editProps?.isSelected}
        cardStyles={cardStyles}
        renderCard={({ index, state, setState, readonly }) => (
          <div class="@lg:p-8 @sm:p-6 p-4 flex flex-col">
            <div class="@lg:mb-6 @sm:mb-4 mb-3 flex-grow service-content">
              <RichProp
                state={state}
                setState={readonly ? undefined : setState}
                path={['cards', index, 'contentRichText']}
              />
            </div>

            <div class="@sm:text-sm text-xs text-gray-600 border-t @lg:pt-4 @sm:pt-3 pt-2 @lg:mb-6 @sm:mb-4 mb-3">
              <RichProp
                state={state}
                setState={readonly ? undefined : setState}
                path={['cards', index, 'durationRichText']}
              />
            </div>

            <footer class="mt-auto flex flex-col">
              <CtaButton
                state={state}
                setState={setState}
                isSelected={false}
                setSelection={() => {}}
                id={`service-${index}-cta`}
                isBgLight={false}
                path={['cards', index, 'callToAction']}
              />
            </footer>
          </div>
        )}
      />
    </ContainerWrapper>
  );
}

export const Editor = View;
export const Viewer = View;
